import { createOntologyHeaders, PROXY_API_V1_URL } from '@axilon-apps/domains-ontology-ui-state'

const SW_URL = '/notification-sw.js'
const SW_SCOPE = '/'

let registrationPromise: Promise<ServiceWorkerRegistration | null> | null = null

function headersRecord(): Record<string, string> {
  const headers = createOntologyHeaders()
  const record: Record<string, string> = {}
  headers.forEach((value, key) => {
    record[key] = value
  })
  return record
}

async function ensureRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return null
  }
  if (!registrationPromise) {
    registrationPromise = navigator.serviceWorker
      .register(SW_URL, { scope: SW_SCOPE })
      .then((reg) => reg)
      .catch(() => null)
  }
  return registrationPromise
}

function postToWorker(
  registration: ServiceWorkerRegistration,
  message: Record<string, unknown>
): void {
  const worker = registration.active ?? registration.waiting ?? registration.installing
  worker?.postMessage(message)
  // Also poke the controller so a just-installed worker receives configure.
  navigator.serviceWorker.controller?.postMessage(message)
}

/** Start / refresh SW polling with current auth headers. */
export async function configureNotificationServiceWorker(enabled: boolean): Promise<void> {
  const registration = await ensureRegistration()
  if (!registration) return

  // Wait briefly for activate so postMessage reaches an active worker.
  if (!registration.active) {
    await navigator.serviceWorker.ready.catch(() => null)
  }

  postToWorker(registration, {
    type: 'configure',
    enabled,
    url: PROXY_API_V1_URL,
    headers: headersRecord(),
    resetSeen: !enabled
  })
}

/** Prefer SW showNotification so OS toasts work while the tab is backgrounded. */
export async function showNotificationViaServiceWorker(options: {
  title: string
  body?: string | null
  tag?: string
}): Promise<boolean> {
  const registration = await ensureRegistration()
  if (!registration) return false
  try {
    await registration.showNotification(options.title, {
      body: options.body?.trim() ? options.body : undefined,
      tag: options.tag,
      requireInteraction: true,
      data: { url: '/notifications' }
    })
    return true
  } catch {
    return false
  }
}
